import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { ErrorCircleRegular } from "@fluentui/react-icons";
import { Button } from "shared-ui-components/fluent/primitives/button";
import { TokenMap } from "shared-ui-components/fluent/primitives/utils";
const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: TokenMap.px20,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        // Claim the full row of the flex parent (e.g. shellService's central-content row)
        // so the centered children sit in the middle of the available area, not at the left edge.
        flex: 1,
        height: "100%",
        minHeight: "100px",
    },
    icon: {
        fontSize: "48px",
        color: tokens.colorPaletteRedForeground1,
        marginBottom: TokenMap.px12,
    },
    title: {
        fontSize: TokenMap.px16,
        fontWeight: "600",
        marginBottom: TokenMap.px8,
    },
    message: {
        fontSize: TokenMap.px12,
        color: tokens.colorNeutralForeground2,
        marginBottom: TokenMap.px16,
        textAlign: "center",
        maxWidth: "300px",
    },
    details: {
        fontSize: TokenMap.px10,
        color: tokens.colorNeutralForeground3,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        maxHeight: "100px",
        overflow: "auto",
        padding: TokenMap.px8,
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: TokenMap.px4,
        marginTop: TokenMap.px8,
        maxWidth: "100%",
    },
});
/**
 * Error boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire application.
 */
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this._handleRetry = () => {
            this.setState({ hasError: false, error: null, errorInfo: null });
        };
        this.state = { hasError: false, error: null, errorInfo: null };
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        // Log the error
        const boundaryName = this.props.name || "ErrorBoundary";
        // eslint-disable-next-line no-console
        console.error(`[${boundaryName}] Error caught:`, error, errorInfo);
        // Call optional error callback
        this.props.onError?.(error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            // If a custom fallback is provided, use it
            if (this.props.fallback) {
                return this.props.fallback;
            }
            // Default fallback UI
            return _jsx(ErrorFallback, { error: this.state.error, onRetry: this._handleRetry });
        }
        return this.props.children;
    }
}
function ErrorFallback({ error, onRetry }) {
    const styles = useStyles();
    return (_jsxs("div", { className: styles.root, children: [_jsx(ErrorCircleRegular, { className: styles.icon }), _jsx("div", { className: styles.title, children: "Something went wrong" }), _jsx("div", { className: styles.message, children: "An error occurred in this component. You can try again or continue using other parts of the tool." }), _jsx(Button, { label: "Try Again", appearance: "primary", onClick: onRetry }), error && _jsx("div", { className: styles.details, children: error.message })] }));
}
//# sourceMappingURL=errorBoundary.js.map